import React, { ReactNode, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Modal as BootstrapModal } from 'react-bootstrap'

import {
  selectContactChange,
  setExistingExternalContact,
  setIsLoadingContact,
  setSelectedKeys,
  setSelectedZocksContact,
  setShouldCreateContact,
  setShouldLinkContact,
  setShowCreateContact,
  setShowLinkContact,
} from '../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

import { getAllKeysInData } from './utils'
import { getIntegrationDisplayName, getIntegrationEntityLabel, IntegrationServiceId } from '../../common/integrations'
import { User } from '../../models/User'
import { Modal } from '../../components/Modal/Modal'
import { ContactUpdateFormValues } from './models'
import './UpdateExternalContactModal.scss'
import { IntegrationIcon } from '../../components/IntegrationIcon'

interface UpdateExternalContactModalProps {
  user: User
  serviceId: IntegrationServiceId
  show: boolean
  onCancel: () => void
  BodyComponent: ReactNode
  FooterComponent: ReactNode
}

export const UpdateExternalContactModal: React.FC<UpdateExternalContactModalProps> = ({ serviceId, user, show, onCancel, BodyComponent, FooterComponent }) => {
  const dispatch = useAppDispatch()

  const contactChange = useAppSelector(selectContactChange)

  const form = useForm<ContactUpdateFormValues>()

  useEffect(() => {
    if (!contactChange) {
      return
    }
    if (!Object.entries(contactChange.contactValues).some(([, value]) => !!value)) {
      form.reset()
    }
    const currentValues = form.getValues('contactValues') || {}
    const newKeys = Object.entries(contactChange.contactValues)
      .filter(([key, value]) => Object.keys(currentValues).includes(key) === false && !!value)
      .map(([key]) => key)

    newKeys.forEach((key) => {
      form.setValue(`contactValues.${key}`, contactChange.contactValues[key], {
        shouldDirty: true,
        shouldValidate: true,
      })
    })

    const keys = getAllKeysInData(contactChange.contactValues, 'contactValues')
    dispatch(setSelectedKeys(keys))
  }, [contactChange, dispatch, form])

  useEffect(() => {
    const integration = user.integrations?.find((e) => e.serviceId === serviceId)
    if (integration) {
      return
    }

    dispatch(setSelectedZocksContact(user))
    dispatch(setIsLoadingContact(false))
    dispatch(setExistingExternalContact(false))

    if (user.type === 'anonymous') {
      dispatch(setShowCreateContact(true))
      dispatch(setShouldCreateContact(true))
      return
    }

    dispatch(setShowLinkContact(true))
    dispatch(setShouldLinkContact(true))
  }, [dispatch, serviceId, user])

  const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)

  const titleAction = `Save ${getIntegrationEntityLabel(serviceId)} To`

  return (
    <FormProvider {...form}>
      <Modal
        dialogClassName='modal-size-75'
        className='modal-xl'
        scrollable={true}
        show={show}
        onHide={onCancel}
        header={
          <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-start'>
            <IntegrationIcon serviceId={serviceId} className='me-2' width='2rem' height='2rem' />
            {titleAction} {integrationDisplayName}
          </BootstrapModal.Title>
        }
        body={BodyComponent}
        footer={FooterComponent}
        footerClassName='d-flex justify-content-between align-items-center'
        centered={true}
        bodyClassName='mt-3 d-flex flex-column'
      />
    </FormProvider>
  )
}
